import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { useQuery } from '@tanstack/react-query';
import { byProductDisplayOrder, MAGAZINE_GROUP_CODE } from 'eds-common';
import type { Application } from '@data/hooks/api/use-applications-api/use-applications-api';

export interface UseApplicationsHierarchyParams {
  /**
   * Narrows the applications to those entitled to one organization in the
   * hierarchy. Omit for every application across the whole hierarchy.
   */
  childOrgId?: string;
}

/**
 * The applications an admin can report on, across their organization hierarchy.
 *
 * Distinct from `useApplications`, which answers for the signed-in user's own
 * entitlements. An admin's nav is built from what the organizations beneath
 * them hold, which is a different question and a different endpoint.
 */
const getApplicationsHierarchy = ({
  childOrgId,
}: UseApplicationsHierarchyParams): Promise<Application[]> =>
  fetcher<Application[]>('/composite/applications/hierarchy', {
    service: '/fulfillment',
    version: '/v2',
    queryParameters: { childOrgId },
  });

export function applicationsHierarchyQuery(
  params: UseApplicationsHierarchyParams = {}
) {
  return {
    queryKey: ['fulfillment-applications-hierarchy', params.childOrgId ?? null],
    queryFn: () => getApplicationsHierarchy(params),
  };
}

const byApplicationDisplayOrder = byProductDisplayOrder<Application>({
  name: (application) => application.applicationLabel,
  isMagazine: (application) =>
    (application.products ?? []).some(
      (product) => product.groupCode === MAGAZINE_GROUP_CODE
    ),
});

export function useApplicationsHierarchy(
  params: UseApplicationsHierarchyParams = {}
) {
  return useQuery({
    ...applicationsHierarchyQuery(params),
    select: (data) => [...data].sort(byApplicationDisplayOrder),
  });
}
